import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AdminBillingReports from './AdminBillingReports';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const statusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (['completed', 'paid', 'active', 'assigned', 'approved'].includes(s)) return 'success';
  if (['pending', 'accrued', 'inactive', 'unassigned', 'expired'].includes(s)) return 'warning';
  if (['failed', 'rejected', 'cancelled'].includes(s)) return 'error';
  return 'default';
};

const SummaryCard = ({ label, value }) => (
  <Card sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ mt: 0.5 }}>{value}</Typography>
  </Card>
);

const SharedFilters = ({
  filters,
  onChange,
  schools,
  agencies,
  showSchool = true,
  showAgency = true,
  showStatus = false,
  statusOptions = ['all', 'active', 'inactive'],
  searchPlaceholder = 'Search...'
}) => (
  <Card sx={{ p: 2, mb: 2 }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={4} md={3}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          placeholder={searchPlaceholder}
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
        />
      </Grid>
      {showSchool && (
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>School</InputLabel>
            <Select
              label="School"
              value={filters.school_id || 'all'}
              onChange={(e) => onChange({ school_id: e.target.value === 'all' ? '' : e.target.value })}
            >
              <MenuItem value="all">All Schools</MenuItem>
              {schools.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      {showAgency && (
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Agency</InputLabel>
            <Select
              label="Agency"
              value={filters.agency_id || 'all'}
              onChange={(e) => onChange({ agency_id: e.target.value === 'all' ? '' : e.target.value })}
            >
              <MenuItem value="all">All Agencies</MenuItem>
              {agencies.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      {showStatus && (
        <Grid item xs={12} sm={4} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status || 'all'}
              onChange={(e) => onChange({ status: e.target.value })}
            >
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} sm={4} md={1}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => onChange({ search: '', school_id: '', agency_id: '', status: 'all' })}
        >
          Clear
        </Button>
      </Grid>
    </Grid>
  </Card>
);

/**
 * Unified Admin Reports hub.
 * section: overview | devices | schools | agencies | students | commissions | commissions-school | commissions-agency | commissions-device | withdrawals
 * onNavigate(section, filterPatch) — parent menu switch with inherited filters
 */
const AdminReportsHub = ({
  section = 'overview',
  schools = [],
  agencies = [],
  devices = [],
  onNavigate,
  onParentRefresh
}) => {
  const token = localStorage.getItem('authToken');
  const [filters, setFilters] = useState({
    search: '',
    school_id: '',
    agency_id: '',
    status: 'all'
  });
  const [overview, setOverview] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [deviceDetail, setDeviceDetail] = useState(null);
  const [entitySummary, setEntitySummary] = useState(null);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const navigate = useCallback((nextSection, filterPatch = {}) => {
    if (Object.keys(filterPatch).length) {
      setFilters((prev) => ({ ...prev, ...filterPatch }));
    }
    if (typeof onNavigate === 'function') {
      onNavigate(nextSection, filterPatch);
    }
  }, [onNavigate]);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reports/overview`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load overview');
      setOverview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.school_id) params.set('school_id', filters.school_id);
      if (filters.agency_id) params.set('agency_id', filters.agency_id);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const res = await fetch(`${API_BASE_URL}/admin/reports/students?${params}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load students');
      setStudents(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filters]);

  const openDeviceDetail = async (deviceKey) => {
    setDetailOpen(true);
    setDeviceDetail(null);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/reports/devices/${encodeURIComponent(deviceKey)}/detail`,
        { headers: authHeaders }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load device details');
      setDeviceDetail(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const openSchoolSummary = async (schoolId) => {
    setEntitySummary(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reports/schools/${schoolId}/summary`, {
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load school summary');
      setEntitySummary({ type: 'school', ...data });
      navigate('students', { school_id: String(schoolId), agency_id: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const openAgencySummary = async (agencyId) => {
    setEntitySummary(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reports/agencies/${agencyId}/summary`, {
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load agency summary');
      setEntitySummary({ type: 'agency', ...data });
      navigate('devices', { agency_id: String(agencyId), school_id: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (section === 'overview') loadOverview();
    if (section === 'students') loadStudents();
    if (onParentRefresh) {
      onParentRefresh();
    }
  }, [section, loadOverview, loadStudents, onParentRefresh]);

  const filteredDevices = useMemo(() => {
    const list = Array.isArray(devices) ? devices : [];
    return list.filter((d) => {
      const q = filters.search.toLowerCase();
      const matchesSearch = !q
        || String(d.device_id || '').toLowerCase().includes(q)
        || String(d.imei_number || '').toLowerCase().includes(q)
        || String(d.school_name || '').toLowerCase().includes(q)
        || String(d.agency_name || '').toLowerCase().includes(q)
        || String(d.sim_number || '').toLowerCase().includes(q);
      const matchesSchool = !filters.school_id || String(d.school_id) === String(filters.school_id);
      const matchesAgency = !filters.agency_id
        || String(d.resolved_agency_id || d.agency_id || '') === String(filters.agency_id);
      const matchesStatus = !filters.status || filters.status === 'all'
        || String(d.status || '').toLowerCase() === filters.status
        || String(d.assignment_status || '').toLowerCase() === filters.status;
      return matchesSearch && matchesSchool && matchesAgency && matchesStatus;
    });
  }, [devices, filters]);

  const filteredSchools = useMemo(() => {
    const list = Array.isArray(schools) ? schools : [];
    return list.filter((s) => {
      const q = filters.search.toLowerCase();
      const matchesSearch = !q
        || String(s.name || '').toLowerCase().includes(q)
        || String(s.email || '').toLowerCase().includes(q)
        || String(s.phone || '').toLowerCase().includes(q);
      const matchesAgency = !filters.agency_id || String(s.agency_id || '') === String(filters.agency_id);
      const matchesStatus = !filters.status || filters.status === 'all'
        || String(s.status || '').toLowerCase() === filters.status;
      return matchesSearch && matchesAgency && matchesStatus;
    });
  }, [schools, filters]);

  const filteredAgencies = useMemo(() => {
    const list = Array.isArray(agencies) ? agencies : [];
    return list.filter((a) => {
      const q = filters.search.toLowerCase();
      const matchesSearch = !q
        || String(a.name || '').toLowerCase().includes(q)
        || String(a.email || '').toLowerCase().includes(q)
        || String(a.phone || '').toLowerCase().includes(q);
      const matchesStatus = !filters.status || filters.status === 'all'
        || String(a.status || '').toLowerCase() === filters.status;
      return matchesSearch && matchesStatus;
    });
  }, [agencies, filters]);

  const renderEntityBanner = () => {
    if (!entitySummary) return null;
    if (entitySummary.type === 'school' && entitySummary.school) {
      const s = entitySummary.school;
      const t = entitySummary.totals || {};
      return (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setEntitySummary(null)}>
          <strong>{s.name}</strong>
          {s.agency_name ? ` · Agency: ${s.agency_name}` : ''}
          {` · Students: ${t.students || 0} · Devices: ${t.devices || 0} · Commission: ${formatMoney(t.commission_total)}`}
          {' · '}
          <Button size="small" onClick={() => navigate('devices', { school_id: String(s.id) })}>
            View devices
          </Button>
          <Button size="small" onClick={() => navigate('commissions-school', { school_id: String(s.id) })}>
            School commission
          </Button>
        </Alert>
      );
    }
    if (entitySummary.type === 'agency' && entitySummary.agency) {
      const a = entitySummary.agency;
      const t = entitySummary.totals || {};
      return (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setEntitySummary(null)}>
          <strong>{a.name}</strong>
          {` · Schools: ${t.schools || 0} · Devices: ${t.devices || 0} · Students: ${t.students || 0} · Commission: ${formatMoney(t.commission_total)}`}
          {' · '}
          <Button size="small" onClick={() => navigate('schools', { agency_id: String(a.id) })}>
            View schools
          </Button>
          <Button size="small" onClick={() => navigate('commissions-agency', { agency_id: String(a.id) })}>
            Agency commission
          </Button>
        </Alert>
      );
    }
    return null;
  };

  const renderOverview = () => (
    <Box>
      {loading && <CircularProgress size={28} sx={{ mb: 2 }} />}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}><SummaryCard label="Schools" value={overview?.schools ?? '—'} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Agencies" value={overview?.agencies ?? '—'} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Devices" value={overview?.devices ?? '—'} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Students" value={overview?.students ?? '—'} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Assigned devices" value={overview?.devices_assigned ?? '—'} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Unassigned devices" value={overview?.devices_unassigned ?? '—'} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Commission accrued" value={formatMoney(overview?.commission_accrued)} /></Grid>
        <Grid item xs={6} md={3}><SummaryCard label="Withdrawals" value={formatMoney(overview?.withdrawals_total)} /></Grid>
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" onClick={() => navigate('devices')}>Devices report</Button>
        <Button variant="outlined" onClick={() => navigate('schools')}>Schools report</Button>
        <Button variant="outlined" onClick={() => navigate('agencies')}>Agencies report</Button>
        <Button variant="outlined" onClick={() => navigate('students')}>Students report</Button>
        <Button variant="outlined" onClick={() => navigate('commissions')}>Commissions</Button>
        <Button variant="outlined" onClick={() => navigate('withdrawals')}>Withdrawals</Button>
      </Stack>
    </Box>
  );

  const renderDevices = () => (
    <Box>
      <SharedFilters
        filters={filters}
        onChange={updateFilters}
        schools={schools}
        agencies={agencies}
        showStatus
        statusOptions={['all', 'active', 'inactive', 'expired', 'assigned', 'unassigned']}
        searchPlaceholder="Device ID, IMEI, SIM, school, agency"
      />
      {renderEntityBanner()}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Showing {filteredDevices.length} of {devices.length} devices
      </Typography>
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>IMEI</TableCell>
                <TableCell>SIM</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Assignment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDevices.map((d) => (
                <TableRow key={d.id || d.device_id}>
                  <TableCell>{d.device_id}</TableCell>
                  <TableCell>{d.imei_number || '—'}</TableCell>
                  <TableCell>{d.sim_number || '—'}</TableCell>
                  <TableCell>{d.school_name || 'Unassigned'}</TableCell>
                  <TableCell>{d.agency_name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={d.assignment_status || '—'} color={statusColor(d.assignment_status)} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={d.status || '—'} color={statusColor(d.status)} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openDeviceDetail(d.device_id)}>More details</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );

  const renderSchools = () => (
    <Box>
      <SharedFilters
        filters={filters}
        onChange={updateFilters}
        schools={schools}
        agencies={agencies}
        showSchool={false}
        showStatus
        searchPlaceholder="School name, email, phone"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Showing {filteredSchools.length} of {schools.length} schools
      </Typography>
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>School</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Students</TableCell>
                <TableCell>Devices</TableCell>
                <TableCell>Activation %</TableCell>
                <TableCell>Renewal %</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSchools.map((s) => {
                const agencyName = agencies.find((a) => String(a.id) === String(s.agency_id))?.name;
                return (
                  <TableRow key={s.id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{agencyName || s.agency_name || '—'}</TableCell>
                    <TableCell>{s.email || '—'}</TableCell>
                    <TableCell>{s.total_students ?? 0}</TableCell>
                    <TableCell>{s.total_devices ?? 0}</TableCell>
                    <TableCell>{s.activation_commission ?? s.sales_commission ?? 0}%</TableCell>
                    <TableCell>{s.renewal_commission ?? s.sales_commission ?? 0}%</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.status || '—'} color={statusColor(s.status)} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openSchoolSummary(s.id)}>More details</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );

  const renderAgencies = () => (
    <Box>
      <SharedFilters
        filters={filters}
        onChange={updateFilters}
        schools={schools}
        agencies={agencies}
        showSchool={false}
        showAgency={false}
        showStatus
        searchPlaceholder="Agency name, email, phone"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Showing {filteredAgencies.length} of {agencies.length} agencies
      </Typography>
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agency</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Activation %</TableCell>
                <TableCell>Renewal %</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAgencies.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{a.email || '—'}</TableCell>
                  <TableCell>{a.phone || '—'}</TableCell>
                  <TableCell>{a.contact_person || '—'}</TableCell>
                  <TableCell>{a.activation_commission ?? a.sales_commission ?? 0}%</TableCell>
                  <TableCell>{a.renewal_commission ?? a.sales_commission ?? 0}%</TableCell>
                  <TableCell>
                    <Chip size="small" label={a.status || '—'} color={statusColor(a.status)} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openAgencySummary(a.id)}>More details</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );

  const renderStudents = () => (
    <Box>
      <SharedFilters
        filters={filters}
        onChange={updateFilters}
        schools={schools}
        agencies={agencies}
        showStatus
        searchPlaceholder="Student, device, school, parent"
      />
      {renderEntityBanner()}
      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Showing {students.length} students
        </Typography>
        <Button size="small" variant="outlined" onClick={loadStudents} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {loading && <CircularProgress size={24} sx={{ mb: 1 }} />}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((st) => (
                <TableRow key={st.id}>
                  <TableCell>{`${st.first_name || ''} ${st.last_name || ''}`.trim()}</TableCell>
                  <TableCell>
                    {[st.standard, st.division].filter(Boolean).join('-') || '—'}
                  </TableCell>
                  <TableCell>{st.device_id || '—'}</TableCell>
                  <TableCell>{st.school_name || '—'}</TableCell>
                  <TableCell>{st.agency_name || '—'}</TableCell>
                  <TableCell>
                    {st.parent_name || '—'}
                    {st.parent_email ? ` (${st.parent_email})` : ''}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={st.status || '—'} color={statusColor(st.status)} />
                  </TableCell>
                  <TableCell align="right">
                    {st.device_id ? (
                      <Button size="small" onClick={() => openDeviceDetail(st.device_id)}>Device</Button>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );

  const commissionGroupBy = {
    commissions: 'detail',
    'commissions-school': 'school',
    'commissions-agency': 'agency',
    'commissions-device': 'device'
  }[section];

  const isCommissionSection = Object.prototype.hasOwnProperty.call(
    {
      commissions: 1,
      'commissions-school': 1,
      'commissions-agency': 1,
      'commissions-device': 1
    },
    section
  );

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {section === 'overview' && renderOverview()}
      {section === 'devices' && renderDevices()}
      {section === 'schools' && renderSchools()}
      {section === 'agencies' && renderAgencies()}
      {section === 'students' && renderStudents()}
      {isCommissionSection && (
        <AdminBillingReports
          schools={schools}
          agencies={agencies}
          initialTab={4}
          initialGroupBy={commissionGroupBy || 'detail'}
          initialSchoolId={filters.school_id}
          initialAgencyId={filters.agency_id}
        />
      )}
      {section === 'withdrawals' && (
        <AdminBillingReports
          schools={schools}
          agencies={agencies}
          initialTab={5}
          initialSchoolId={filters.school_id}
          initialAgencyId={filters.agency_id}
        />
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Device details</DialogTitle>
        <DialogContent>
          {!deviceDetail ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Device ID</Typography>
                <Typography>{deviceDetail.device?.device_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">IMEI</Typography>
                <Typography>{deviceDetail.device?.imei_number || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">SIM</Typography>
                <Typography>{deviceDetail.device?.sim_number || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip size="small" label={deviceDetail.device?.status || '—'} color={statusColor(deviceDetail.device?.status)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">School</Typography>
                <Typography>{deviceDetail.device?.school_name || 'Unassigned'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Agency</Typography>
                <Typography>{deviceDetail.device?.agency_name || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Subscription</Typography>
                <Typography>
                  {deviceDetail.subscription
                    ? `${deviceDetail.subscription.plan_name || 'Plan'} · ${deviceDetail.subscription.status || ''} · ends ${deviceDetail.subscription.end_date || '—'}`
                    : 'No subscription'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Last location</Typography>
                <Typography>
                  {deviceDetail.last_location
                    ? `${deviceDetail.last_location.latitude}, ${deviceDetail.last_location.longitude} · bat ${deviceDetail.last_location.battery ?? '—'}%`
                    : 'No location yet'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Assigned students ({deviceDetail.students?.length || 0})
                </Typography>
                {(deviceDetail.students || []).length === 0 ? (
                  <Typography variant="body2">No student linked</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Parent</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deviceDetail.students.map((st) => (
                        <TableRow key={st.id}>
                          <TableCell>{`${st.first_name || ''} ${st.last_name || ''}`.trim()}</TableCell>
                          <TableCell>{[st.standard, st.division].filter(Boolean).join('-') || '—'}</TableCell>
                          <TableCell>{st.parent_email || st.parent_name || '—'}</TableCell>
                          <TableCell>{st.status || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {deviceDetail?.device?.school_id ? (
            <Button onClick={() => {
              setDetailOpen(false);
              navigate('students', { school_id: String(deviceDetail.device.school_id) });
            }}>
              School students
            </Button>
          ) : null}
          {deviceDetail?.device?.agency_id ? (
            <Button onClick={() => {
              setDetailOpen(false);
              navigate('devices', { agency_id: String(deviceDetail.device.agency_id) });
            }}>
              Agency devices
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminReportsHub;
